/* RAPTIC hero slider: auto-playing crossfade carousel. */
class RapticHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.raptic-hero__slide'));
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('.raptic-hero__dot'));
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.index = 0;
    this.timer = null;

    this.style.setProperty('--raptic-hero-interval', this.interval + 'ms');

    const prev = this.querySelector('.raptic-hero__arrow--prev');
    const next = this.querySelector('.raptic-hero__arrow--next');
    if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1, true));
    if (next) next.addEventListener('click', () => this.goTo(this.index + 1, true));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    // Pause autoplay when off-screen or when the user prefers reduced motion
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.play();
            else this.pause();
          });
        },
        { threshold: 0.2 }
      ).observe(this);
    } else {
      this.play();
    }

    // Scroll CTA → smooth-scroll to the model finder section
    const cta = this.querySelector('[data-raptic-scroll]');
    if (cta) {
      cta.addEventListener('click', (e) => {
        const target =
          document.getElementById('raptic-model-finder') ||
          document.querySelector('.raptic-finder-section');
        if (!target) return; // fall back to the plain anchor navigation
        e.preventDefault();
        target.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      });
    }

    // Touch swipe
    let startX = null;
    this.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
    this.addEventListener(
      'touchend',
      (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.goTo(this.index + (dx < 0 ? 1 : -1), true);
        startX = null;
      },
      { passive: true }
    );

    this.render();
  }

  disconnectedCallback() {
    this.pause();
  }

  goTo(i, userInitiated) {
    this.index = (i + this.slides.length) % this.slides.length;
    this.render();
    if (userInitiated) this.play(); // restart the timer after manual navigation
  }

  render() {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.index);
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
  }

  play() {
    this.pause();
    if (this.reducedMotion || this.slides.length < 2) return;
    this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
  }

  pause() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('raptic-hero-slider')) {
  customElements.define('raptic-hero-slider', RapticHeroSlider);
}
